import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { SalaryInputComponent, TagComponent } from '@catch/general-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, tap } from 'rxjs';
import {
  PLATFORM_PROFILE_CURRENCIES,
  PLATFORM_PROFILE_WORKING_MODELS,
} from '../../../../models/platform.models';
import { PlatformAvailability } from '../../../../enums/platform-availability.enum';
import { PlatformLocationsService } from '../../../../services/platform-locations.service';

@Component({
  selector: 'app-platform-profile-preferences',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    NgSelectModule,
    SalaryInputComponent,
    TagComponent,
  ],
  templateUrl: './platform-profile-preferences.component.html',
  styleUrl: './platform-profile-preferences.component.scss',
})
export class PlatformProfilePreferencesComponent implements OnInit {
  private readonly locations = inject(PlatformLocationsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly form = input.required<FormGroup>();
  readonly submitAttempt = input(false);

  readonly currencies = PLATFORM_PROFILE_CURRENCIES;
  readonly workingModels = PLATFORM_PROFILE_WORKING_MODELS;
  readonly availabilityOptions = Object.values(PlatformAvailability);
  private readonly citySearchResults = signal<string[]>([]);
  /** Selected preferred cities — kept in items so typeahead never drops tags. */
  private readonly selectedCities = signal<string[]>([]);
  readonly cityItems = computed((): string[] => {
    const selected = this.selectedCities();
    const results = this.citySearchResults();
    const items = [...selected];
    for (const city of results) {
      if (!items.includes(city)) {
        items.push(city);
      }
    }
    return items;
  });
  readonly citiesLoading = signal(false);
  readonly cityInput$ = new Subject<string>();

  constructor() {
    effect((onCleanup) => {
      const form = this.form();
      this.syncSelectedCities(form.controls['preferredLocations'].value);
      const sub = form.controls['preferredLocations'].valueChanges.subscribe((value) =>
        this.syncSelectedCities(value)
      );
      onCleanup(() => sub.unsubscribe());
    });
  }

  ngOnInit(): void {
    this.cityInput$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => this.citiesLoading.set(true)),
        switchMap((term) => {
          const query = term?.trim() ?? '';
          if (query.length < 2) {
            this.citiesLoading.set(false);
            return of(this.citySearchResults());
          }
          return this.locations.searchCities(query).pipe(
            catchError(() => of([] as string[])),
            tap(() => this.citiesLoading.set(false))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((cities) => this.citySearchResults.set(cities));
  }

  /**
   * Keeps selected preferred cities in sync with the form control.
   * @param value - Preferred location names from the form
   */
  private syncSelectedCities(value: unknown): void {
    const cities = Array.isArray(value)
      ? value.filter((city): city is string => typeof city === 'string' && !!city.trim())
      : [];
    this.selectedCities.set(cities);
  }

  /**
   * Returns the min salary control for the shared salary input.
   * @returns Salary min control
   */
  salaryMinControl(): UntypedFormControl {
    return this.form().controls['salaryMin'] as UntypedFormControl;
  }

  /**
   * Returns the max salary control for the shared salary input.
   * @returns Salary max control
   */
  salaryMaxControl(): UntypedFormControl {
    return this.form().controls['salaryMax'] as UntypedFormControl;
  }

  /**
   * Returns the translation key for a working model.
   * @param model - Working model value
   * @returns Translation key
   */
  workingModelKey(model: string): string {
    return `jobboard.platform.profile.working-model.${model}`;
  }

  /**
   * Returns the translation key for an availability option.
   * @param value - Availability value
   * @returns Translation key
   */
  availabilityKey(value: string): string {
    return `jobboard.platform.profile.availability.${value}`;
  }
}
